import { useTranslation } from 'react-i18next';
import PersonContractHeading from './PersonContractHeading';
import PersonContractBody from './PersonContractBody';
import { useState, useEffect } from 'react';
import { majorContractsService } from '@shared/services/majorContracts';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import DocumentNumberChange from './DocumentNumberChange';
import ContractSalaryCalc from './ContractSalaryCalc';
import ContractWordView from './ContractWordView';
import CollectData from './CollectData';

export default function PersonContractForm({ person, activeForm, setActiveForm, onClose, initialContractType = 'laborContract', initialSelectedMainContract = '', initialSelectedContractId = null }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [contractType, setContractType] = useState(initialContractType);
    const [selectedMainContract, setSelectedMainContract] = useState(() => initialSelectedMainContract ?? '');
    const [newIconClick, setNewIconClick] = useState(0);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isCreateModeActive, setIsCreateModeActive] = useState(false);
    const [editingContractId, setEditingContractId] = useState(null);
    const [overlayView, setOverlayView] = useState(null);
    const [contractNo, setContractNo] = useState('');
    const [selectedContractId, setSelectedContractId] = useState(null);
    const [selectedContractPositionTypeId, setSelectedContractPositionTypeId] = useState(null);
    const [mainContracts, setMainContracts] = useState([]);
    const [contractRefreshToken, setContractRefreshToken] = useState(0);

    const refreshContractData = () => {
        setContractRefreshToken((prev) => prev + 1);
    };

    useEffect(() => {
        setContractType(initialContractType || 'laborContract');
    }, [initialContractType]);

    // Only apply non-empty restores; avoid wiping with '' when person prop refreshes without the field.
    useEffect(() => {
        if (initialSelectedMainContract !== undefined && initialSelectedMainContract !== null && String(initialSelectedMainContract).length > 0) {
            setSelectedMainContract(initialSelectedMainContract);
        }
    }, [initialSelectedMainContract]);

    useEffect(() => {
        if (initialSelectedContractId !== undefined && initialSelectedContractId !== null && String(initialSelectedContractId).length > 0) {
            setSelectedContractId(initialSelectedContractId);
        }
    }, [initialSelectedContractId]);

    const handleNewIconClick = () => {
        setNewIconClick(prev => {
            const newValue = prev + 1;
            return newValue;
        });
    };

    const handleActivateContract = async () => {
        const contractId = editingContractId || selectedContractId;
        if (!contractId) {
            notifyError(t('persons.personContract.selectContractFirst') || 'Please select a contract first.');
            return;
        }
        try {
            await majorContractsService.activateContract(contractId);
            notifySuccess(t('common.updatedSuccessfully') || 'Updated successfully');
            refreshContractData();
        } catch (error) {
            console.error('Error activating contract:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    const handleConfirmContract = async () => {
        const contractId = editingContractId || selectedContractId;
        if (!contractId) {
            notifyError(t('persons.personContract.selectContractFirst') || 'Please select a contract first.');
            return;
        }
        try {
            await majorContractsService.confirmContract(contractId);
            notifySuccess("Потвърден успешно" || 'Updated successfully');
            refreshContractData();
        } catch (error) {
            console.error('Error confirming contract:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    const handleEraseContract = async () => {
        const contractId = editingContractId || selectedContractId;
        if (!contractId) {
            notifyError(t('persons.personContract.selectContractFirst') || 'Please select a contract first.');
            return;
        }
        try {
            await majorContractsService.eraseContract(contractId);
            notifySuccess(t('common.updatedSuccessfully') || 'Updated successfully');
            refreshContractData();
        } catch (error) {
            console.error('Error erasing contract:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    const handleRemoveContract = async () => {
        const contractId = editingContractId || selectedContractId;
        if (!contractId) {
            notifyError(t('persons.personContract.selectContractFirst') || 'Please select a contract first.');
            return;
        }
        try {
            await majorContractsService.removeContract(contractId);
            notifySuccess(t('common.deletedSuccessfully') || 'Deleted successfully');
            if (String(editingContractId) === String(contractId)) {
                setEditingContractId(null);
                setIsEditMode(false);
                setIsCreateModeActive(false);
            }
            if (String(selectedContractId) === String(contractId)) {
                setSelectedContractId(null);
            }
            setSelectedContractPositionTypeId(null);
            refreshContractData();
        } catch (error) {
            console.error('Error removing contract:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    const handleRefuseContract = async () => {
        const contractId = editingContractId || selectedContractId;
        if (!contractId) {
            notifyError(t('persons.personContract.selectContractFirst') || 'Please select a contract first.');
            return;
        }
        try {
            await majorContractsService.refuseContract(contractId);
            notifySuccess(t('persons.personContract.napRefuseSuccess') || 'Успешно отказване от НАП');
            refreshContractData();
        } catch (error) {
            console.error('Error refusing contract (NAP):', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    const handleSaveContractNo = async () => {
        const contractIdToUpdate = editingContractId || selectedContractId;
        if (!contractIdToUpdate) {
            notifyError(t('persons.personContract.selectContractFirst') || 'Please select a contract first.');
            return;
        }

        setLoading(true);
        try {
            await majorContractsService.changeContractNumber(contractIdToUpdate, contractNo);

            const updatedContract = await majorContractsService.getContractById(contractIdToUpdate);
            if (updatedContract?.contractNo !== undefined) {
                setContractNo(updatedContract.contractNo);
            }

            notifySuccess(t('common.updatedSuccessfully') || 'Updated successfully');
            setOverlayView(null);
        } catch (error) {
            console.error('Error updating contract number:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        } finally {
            setLoading(false);
        }
    };

    const handleExportIdn = async () => {
        const contractId = editingContractId || selectedContractId;
        if (!contractId) {
            notifyError(t('persons.personContract.selectContractFirst') || 'Please select a contract first.');
            return;
        }

        try {
            await majorContractsService.generateIdmInterface(contractId);
            notifySuccess('Успешно генериране на IDN');
        } catch (error) {
            console.error('Error generating IDN:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    const handleSendFirstEmail = async () => {
        const contractId = editingContractId || selectedContractId;
        if (!contractId) {
            notifyError(t('persons.personContract.selectContractFirst') || 'Please select a contract first.');
            return;
        }

        try {
            await majorContractsService.sendFirstEmail(contractId);
            notifySuccess(t('persons.personContract.firstEmailSentSuccess'));
        } catch (error) {
            console.error('Error sending first email:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    const documentChange = overlayView === 'contractDocumentNum';
    const showCalcForm = overlayView === 'contractCalc';
    const showWordForm = overlayView === 'contractWord';
    const showCollectDataForm = overlayView === 'contractPersonCollect';

    return (
        <div className="main-person-form-container">
            {documentChange ? (
                   <DocumentNumberChange
                        contractNo={contractNo}
                        setContractNo={setContractNo}
                        handleSaveContractNo={handleSaveContractNo}
                        loading={loading}
                        editingContractId={editingContractId}
                        selectedContractId={selectedContractId}
                        setOverlayView={setOverlayView}
                    />
            ) : showCalcForm ? (
                <ContractSalaryCalc setOverlayView={setOverlayView} />
            ) : showWordForm ? (
                <ContractWordView
                    setOverlayView={setOverlayView}
                    contractType={contractType}
                    contractId={editingContractId || selectedContractId}
                />
            ) : showCollectDataForm ? (
                <CollectData
                    setOverlayView={setOverlayView}
                    person={person}
                />
            ) : (
                <>
                    <PersonContractHeading
                        activeForm={activeForm}
                        setActiveForm={setActiveForm}
                        person={person}
                        onClose={onClose}
                        contractType={contractType}
                        setContractType={setContractType}
                        selectedMainContract={selectedMainContract}
                        setSelectedMainContract={setSelectedMainContract}
                        onMainContractsLoaded={setMainContracts}
                        onNewIconClick={handleNewIconClick}
                        onOverlayIconClick={setOverlayView}
                        onActivateClick={handleActivateContract}
                        onConfirmClick={handleConfirmContract}
                        onEraseClick={handleEraseContract}
                        onRemoveClick={handleRemoveContract}
                        onRefuseClick={handleRefuseContract}
                        onExportClick={handleExportIdn}
                        onMailSentClick={handleSendFirstEmail}
                        isEditMode={isEditMode}
                        isCreateModeActive={isCreateModeActive}
                        editingContractId={editingContractId}
                        onCancelAddMode={() => setIsCreateModeActive(false)}
                        onCancelEditMode={() => {
                            setEditingContractId(null);
                            setIsEditMode(false);
                            setIsCreateModeActive(false);
                            setSelectedContractPositionTypeId(null);
                        }}
                        selectedContractId={selectedContractId}
                        selectedContractPositionTypeId={selectedContractPositionTypeId}
                    />
                    <PersonContractBody
                        contractType={contractType}
                        person={person}
                        selectedMainContract={selectedMainContract}
                        initialSelectedContractId={initialSelectedContractId}
                        mainContracts={mainContracts}
                        setContractType={setContractType}
                        setSelectedMainContract={setSelectedMainContract}
                        onNewIconClick={newIconClick}
                        setIsEditMode={setIsEditMode}
                        setIsCreateModeActive={setIsCreateModeActive}
                        isCreateModeActive={isCreateModeActive}
                        setEditingContractId={setEditingContractId}
                        selectedContractId={selectedContractId}
                        setSelectedContractId={setSelectedContractId}
                        setSelectedContractPositionTypeId={setSelectedContractPositionTypeId}
                        contractRefreshToken={contractRefreshToken}
                        contractNo={contractNo}
                        setContractNo={setContractNo}
                        onClose={onClose}
                    />
                </>
            )}
        </div>
    );
}